import { coercePref } from './coerce-pref.util';

export type DateKind = 'date' | 'datetime' | 'time';
export type DateFormatPref = 'system' | 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd';
export type TimeFormatPref = 'system' | '24h' | '12h';

export const DATE_FORMAT_KEY = 'display.dateFormat';
export const TIME_FORMAT_KEY = 'display.timeFormat';

// `LOCALE_ID` is hardcoded 'it', so these are the current "system" look.
const SYSTEM_DATE_PATTERN = 'dd/MM/yyyy';
const SYSTEM_TIME_PATTERN = 'HH:mm';

const DATE_ONLY_PRESETS = ['shortDate', 'mediumDate', 'longDate', 'fullDate'];
const TIME_ONLY_PRESETS = ['shortTime', 'mediumTime', 'longTime', 'fullTime'];
const DATETIME_PRESETS = ['short', 'medium', 'long', 'full'];

/** Granularity a call site intends, inferred from its (Angular) fallback format. */
export function inferKind(fallback: string): DateKind {
  if (TIME_ONLY_PRESETS.includes(fallback)) return 'time';
  if (DATE_ONLY_PRESETS.includes(fallback)) return 'date';
  if (DATETIME_PRESETS.includes(fallback)) return 'datetime';
  const hasTime = /[Hhms]/.test(fallback);
  const hasDate = /[yMd]/.test(fallback);
  if (hasTime) return hasDate ? 'datetime' : 'time';
  return 'date';
}

/**
 * Resolve the Angular format string to render with.
 * When both prefs are 'system' the call site's fallback is returned verbatim, so
 * untouched-by-the-user displays are pixel-identical to today. Once either pref is
 * concrete we switch to explicit patterns; the 'system' half then uses the locale
 * default ('dd/MM/yyyy' / 'HH:mm').
 */
export function resolveFormat(
  fallback: string,
  datePref: DateFormatPref,
  timePref: TimeFormatPref,
  kindOverride?: DateKind,
): string {
  if (datePref === 'system' && timePref === 'system') return fallback;

  const kind = kindOverride ?? inferKind(fallback);
  const datePattern = datePref === 'system' ? SYSTEM_DATE_PATTERN : datePref;
  const timePattern = timePref === '12h' ? 'h:mm a' : SYSTEM_TIME_PATTERN;

  switch (kind) {
    case 'date':
      return datePattern;
    case 'time':
      return timePattern;
    default:
      return `${datePattern} ${timePattern}`;
  }
}

export function coerceDateFormat(v: unknown): DateFormatPref {
  return coercePref(v, ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd'], 'system');
}

export function coerceTimeFormat(v: unknown): TimeFormatPref {
  return coercePref(v, ['24h', '12h'], 'system');
}

export type FirstDayOfWeekPref = 'sunday' | 'monday';

export const FIRST_DAY_OF_WEEK_KEY = 'display.firstDayOfWeek';

export function coerceFirstDayOfWeek(v: unknown): FirstDayOfWeekPref {
  return coercePref(v, ['monday'], 'sunday');
}
